import React, { ReactNode } from "react";
import { classNames } from "primereact/utils";
import styles from "./ListCard.module.css";
import { IconType } from "react-icons";

// --- Types ---

export interface ListCardProps {
  children: ReactNode;
  /** Opacity reduce for inactive states */
  inactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export interface ListCardHeaderProps {
  /** Icon representation. Accepts icon name/component and standard severity color. */
  icon?: { 
    name: string | IconType;
    severity?: "primary" | "secondary" | "success" | "warning" | "danger" | "info" | string;
    bgColor?: string;
    color?: string;
  };
  /** Custom avatar/image node rendered in place of the icon area. Takes priority over `icon`. */
  avatar?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Small identifier, e.g. "FMEA-123" */
  identifier?: ReactNode;
  /** Reserved for arbitrary tags on the right/bottom */
  children?: ReactNode;
  className?: string;
}

export interface ListCardContentProps {
  /** Should contain Description, Metadata, or other custom views */
  children: ReactNode;
  className?: string;
}

export interface ListCardDescriptionProps {
  children: ReactNode;
  className?: string;
}

export interface ListCardMetadataProps {
  children: ReactNode;
  /** 
   * stacked: Vertical standard layout with icons.
   * inline: Horizontal compact layout.
   */
  layout?: "stacked" | "inline";
  className?: string;
}

export interface ListCardMetadataItemProps {
  icon?: string | IconType;
  label?: string;
  value: ReactNode;
  iconColor?: string;
  className?: string;
}

export interface ListCardFooterProps {
  children: ReactNode;
  /** "between" is useful when mixing metrics/identifiers on the left and actions on the right */
  alignment?: "start" | "center" | "end" | "between";
  /** Add a top border to visually separate content from actions */
  divider?: boolean;
  className?: string;
}

// --- Helpers ---
const renderIcon = (icon: string | IconType | undefined, color?: string): React.ReactNode => {
  if (!icon) return null;
  const iconStyle = color ? { color } : undefined;
  
  // If it's a string, we assume PrimeIcons or Material Symbol
  if (typeof icon === "string") {
    if (icon.startsWith("pi ") || icon.startsWith("fas ") || icon.startsWith("far ")) {
      return <i className={classNames(icon, styles.iconBase)} style={iconStyle} aria-hidden="true" />;
    }
    // Default material symbol
    return <span className={classNames(styles.iconBase, "material-symbols-outlined")} style={iconStyle} aria-hidden="true">{icon}</span>;
  }

  const IconComponent = icon as IconType;
  return <IconComponent className={styles.iconBase} style={iconStyle} aria-hidden="true" />;
};

const resolveSeverityColor = (severity?: string): string | undefined => {
  switch (severity) {
    case "primary": return "var(--primary-color, #16a74e)";
    case "secondary": return "var(--secondary-color, #64748b)";
    case "success": return "var(--success-color, #10b981)";
    case "warning": return "var(--warning-color, #f59e0b)";
    case "danger": return "var(--danger-color, #ef4444)";
    case "info": return "var(--info-color, #3b82f6)";
    default: return severity; // Fallback to raw string if passed a HEX directly (though not recommended)
  }
};

// --- Subcomponents ---

const Root: React.FC<ListCardProps> = ({ children, inactive, className, style }) => (
  <article 
    className={classNames(styles.card, { [styles.inactive]: inactive }, className)}
    style={style}
  >
    {children}
  </article>
);

const Header: React.FC<ListCardHeaderProps> = ({ 
  icon, avatar, title, subtitle, identifier, children, className 
}) => {
  let leadingArea: React.ReactNode = null;

  if (avatar) {
    // Avatar takes priority — render the passed node directly
    leadingArea = avatar;
  } else if (icon) {
    const defaultColor = resolveSeverityColor(icon.severity) || "var(--text-color, #1f2937)";
    const bgColor = icon.bgColor || `color-mix(in srgb, ${defaultColor} 15%, transparent)`;
    const color = icon.color || defaultColor;

    leadingArea = (
      <div className={styles.headerIconWrapper} style={{ backgroundColor: bgColor, color }}>
        {renderIcon(icon.name)}
      </div>
    );
  }

  return (
    <header className={classNames(styles.header, className)}>
      {leadingArea}
      
      <div className={styles.headerContent}>
        <div className={styles.headerMain}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{title}</h3>
            {identifier && <span className={styles.identifier}>{identifier}</span>}
          </div>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      </div>
      
      {children && (
        <div className={styles.headerActions}>
          {children}
        </div>
      )}
    </header>
  );
};

const Content: React.FC<ListCardContentProps> = ({ children, className }) => (
  <div className={classNames(styles.content, className)}>
    {children}
  </div>
);

const Description: React.FC<ListCardDescriptionProps> = ({ children, className }) => (
  <div className={classNames(styles.description, className)}>
    {children}
  </div>
);

const Metadata: React.FC<ListCardMetadataProps> = ({ children, layout = "stacked", className }) => (
  <div className={classNames(
    styles.metadata, 
    layout === "stacked" ? styles.metadataStacked : styles.metadataInline,
    className
  )}>
    {children}
  </div>
);

const MetadataItem: React.FC<ListCardMetadataItemProps> = ({ icon, label, value, iconColor, className }) => (
  <div className={classNames(styles.metadataItem, className)}>
    {renderIcon(icon, resolveSeverityColor(iconColor))}
    <div className={styles.metadataItemContent}>
      {label && <span className={styles.metadataLabel}>{label}</span>}
      <span className={styles.metadataValue}>{value}</span>
    </div>
  </div>
);

const Footer: React.FC<ListCardFooterProps> = ({ children, alignment = "between", divider, className }) => (
  <footer className={classNames(
    styles.footer, 
    { [styles.footerDivider]: divider }, 
    styles[`footerAlign${alignment.charAt(0).toUpperCase() + alignment.slice(1)}`], 
    className
  )}>
    {children}
  </footer>
);

// --- Compound Export ---
export const ListCard = Object.assign(Root, {
  Header,
  Content,
  Description,
  Metadata: Object.assign(Metadata, { Item: MetadataItem }),
  Footer
});

export default ListCard;
